import React, { useState, useMemo, useCallback } from 'react'
import { AiOutlineSearch, AiOutlineCaretDown } from 'react-icons/ai'
import { BsArrowRight } from 'react-icons/bs'
import { HiOutlineBookOpen } from 'react-icons/hi'
import { useDispatch, useSelector } from 'react-redux'
import history from 'services/history'
import { learningApi } from 'services/api'
import { RootState } from 'store/modules/rootReducer'
import { editPreference } from 'store/modules/student/actions'
import { IAccessContentsRes } from 'pages/Course/components/ModuleAcordeon/types'
import { IStudyPlan } from 'store/modules/studyPlan/types'
import { toast } from 'react-toastify'
import ProgressCircle from 'components/ProgressCircle'
import { planningApi } from 'pages/Planning/services/api'
import { useFreemiumModal } from 'hooks/use-freemium-modal'

interface StructuredContestProps {
  plan: IStudyPlan
  course: any
  isDarkMode?: boolean
  isLoading?: boolean
}

type ViewMode = 'my-cycle' | 'full-syllabus'

interface LessonProgressData {
  ref: string
  name: string
  heading: string
  order: number
  progress: number
  completed: boolean
  available: { lock: boolean }
  first?: {
    course: { _id: string }
    module: { _id: string }
    lesson: { _id: string }
    chapter: { _id: string }
    objects: string[]
  }
  last?: {
    course: { _id: string }
    module: { _id: string }
    lesson: { _id: string }
    chapter: { _id: string }
    object?: { type: string }
  }
}

interface ModuleProgressResponse {
  children: LessonProgressData[]
  progress: number
  completedLessons: number
}

interface ModuleWithChildren {
  _id: string
  ref: string
  name: string
  subject: string
  order: number
  progress?: number
  children: {
    ref: string
    name: string
    heading: string
    order: number
    progress?: number
    completed?: boolean
  }[]
}

const StructuredContest: React.FC<StructuredContestProps> = ({
  plan,
  course,
  isDarkMode = false,
  isLoading = false,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('my-cycle')
  const [searchFilter, setSearchFilter] = useState('')
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())
  const [loadingLessonRef, setLoadingLessonRef] = useState<string | null>(null)
  const [moduleProgressMap, setModuleProgressMap] = useState<Map<string, ModuleProgressResponse>>(
    new Map()
  )
  const [loadingModules, setLoadingModules] = useState<Set<string>>(new Set())
  const [selectedModuleId, setSelectedModuleId] = useState<string | undefined>(undefined)
  const dispatch = useDispatch()
  const { profile } = useSelector((state: RootState) => state.student)
  const { token } = useSelector((state: RootState) => state.auth)

  // Hook para gerenciar o modal do freemium
  const freemiumModal = useFreemiumModal({
    course,
    moduleId: selectedModuleId,
  })

  // Obter módulos do plano de estudos (MEU CICLO)
  const planModules = useMemo(() => {
    if (!plan?.modules || !course?.children) return []

    const planModuleIds = plan.modules.map((m) => m._id)

    return course.children
      .filter((module: any) => planModuleIds.includes(module.ref))
      .map((module: any) => ({
        _id: module.ref,
        ref: module.ref,
        name: module.name,
        subject: module.subject || module.name,
        order: module.order,
        progress: module.progress || 0,
        children: (module.children || []).sort((a, b) => a.order - b.order),
      }))
      .sort((a: ModuleWithChildren, b: ModuleWithChildren) => a.order - b.order)
  }, [plan?.modules, course?.children])

  // Obter todos os módulos do curso (TODO O EDITAL)
  const allModules = useMemo(() => {
    if (!course?.children) return []

    return course.children
      .map((module: any) => ({
        _id: module.ref,
        ref: module.ref,
        name: module.name,
        subject: module.subject || module.name,
        order: module.order,
        progress: module.progress || 0,
        children: (module.children || []).sort((a, b) => a.order - b.order),
      }))
      .sort((a: ModuleWithChildren, b: ModuleWithChildren) => a.order - b.order)
  }, [course?.children])

  // Módulos filtrados baseado no modo de visualização e busca
  const filteredModules = useMemo(() => {
    const modules = viewMode === 'my-cycle' ? planModules : allModules

    if (!searchFilter.trim()) return modules

    const searchTerm = searchFilter.toLowerCase().trim()

    return modules
      .map((module: ModuleWithChildren) => {
        const subjectMatch = module.subject?.toLowerCase().includes(searchTerm)
        const filteredChildren = module.children.filter((child) =>
          child.heading?.toLowerCase().includes(searchTerm)
        )

        // Se o subject da disciplina combina ou tem aulas que combinam
        if (subjectMatch || filteredChildren.length > 0) {
          return {
            ...module,
            children: subjectMatch ? module.children : filteredChildren,
          }
        }
        return null
      })
      .filter(Boolean)
  }, [viewMode, planModules, allModules, searchFilter])

  // Calcular estatísticas
  const stats = useMemo(() => {
    const modules = viewMode === 'my-cycle' ? planModules : allModules
    const totalModules = modules.length
    const totalLessons = modules.reduce(
      (sum: number, m: ModuleWithChildren) => sum + m.children.length,
      0
    )
    const completedLessons = modules.reduce(
      (sum: number, m: ModuleWithChildren) => sum + m.children.filter((c) => c.completed).length,
      0
    )

    return {
      totalModules,
      totalLessons,
      completedLessons,
      progress: totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0,
    }
  }, [viewMode, planModules, allModules])

  const fetchModuleProgress = useCallback(
    async (moduleId: string) => {
      if (!plan.course?._id || moduleProgressMap.has(moduleId) || loadingModules.has(moduleId)) {
        return
      }

      setLoadingModules((prev) => new Set(prev).add(moduleId))

      try {
        const response = await planningApi.getProgressInfoByModuleId(plan.course._id, moduleId)
        setModuleProgressMap((prev) => new Map(prev).set(moduleId, response))
      } catch (err) {
        toast.error('Erro ao carregar progresso das aulas')
        console.error('Error fetching module progress:', err)
      } finally {
        setLoadingModules((prev) => {
          const next = new Set(prev)
          next.delete(moduleId)
          return next
        })
      }
    },
    [plan.course?._id, moduleProgressMap, loadingModules]
  )

  const toggleModule = useCallback(
    (moduleId: string) => {
      setExpandedModules((prev) => {
        const next = new Set(prev)
        if (next.has(moduleId)) {
          next.delete(moduleId)
        } else {
          next.add(moduleId)
          // Fetch progress when expanding for the first time
          fetchModuleProgress(moduleId)
        }
        return next
      })
    },
    [fetchModuleProgress]
  )

  const handleAccessContent = useCallback(
    async (moduleRef: string, lessonRef: string) => {
      if (!plan.course?._id) return

      setLoadingLessonRef(lessonRef)

      try {
        const headers = {
          Authorization: `Bearer ${token}`,
        }
        const { data } = await learningApi.get<IAccessContentsRes>(
          `/course/${plan.course._id}/module/${moduleRef}/access-content`,
          { headers }
        )

        const contentExists = data?.lastViewed && data?.lastViewed?.lesson?._id === lessonRef

        if (contentExists && data.lastViewed) {
          // Continuar de onde parou
          const item = data.lastViewed
          if (item?.object?.type) dispatch(editPreference(item.object.type !== 'video' ? 1 : 0))
          const url = `/course/${item.course._id}/module/${item.module._id}/lesson/${item.lesson._id}/chapter/${item.chapter._id}`
          window.open(url, '_blank')
        } else {
          // Começar do início
          const content = data.contents.find(
            (content) => content.hasContent && content.data?.lesson._id === lessonRef
          )?.data
          if (content) {
            if (content?.objects?.every((o) => o.match(/video/gi))) dispatch(editPreference(0))
            if (content?.objects?.every((o) => o.match(/excerto/gi))) dispatch(editPreference(1))
            const url = `/course/${content.course._id}/module/${content.module._id}/lesson/${content.lesson._id}/chapter/${content.chapter._id}`
            window.open(url, '_blank')
          }
        }
      } catch (err) {
        toast.error('Ocorreu um erro ao carregar o conteúdo da aula')
      } finally {
        setLoadingLessonRef(null)
      }
    },
    [plan.course?._id, token, dispatch]
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilter(e.target.value)
  }

  // Get lesson progress data from moduleProgressMap
  const getLessonProgressData = useCallback(
    (moduleId: string, lessonRef: string): LessonProgressData | null => {
      const moduleProgress = moduleProgressMap.get(moduleId)
      if (!moduleProgress?.children) return null
      return moduleProgress.children.find((child) => child.ref === lessonRef) || null
    },
    [moduleProgressMap]
  )

  // Handle navigation based on progress
  const handleLessonNavigation = useCallback(
    (moduleRef: string, lessonRef: string) => {
      const progressData = getLessonProgressData(moduleRef, lessonRef)

      if (progressData?.progress && progressData.progress > 0 && progressData.last) {
        // Continue from where user left off
        const { course, module, lesson, chapter, object } = progressData.last
        if (object?.type) dispatch(editPreference(object.type !== 'video' ? 1 : 0))
        window.open(
          `/course/${course._id}/module/${module._id}/lesson/${lesson._id}/chapter/${chapter._id}`
        )
      } else if (progressData?.first) {
        // Start from beginning
        const { course, module, lesson, chapter, objects } = progressData.first
        if (objects?.every((o) => o.match(/video/gi))) dispatch(editPreference(0))
        if (objects?.every((o) => o.match(/excerto/gi))) dispatch(editPreference(1))
        window.open(
          `/course/${course._id}/module/${module._id}/lesson/${lesson._id}/chapter/${chapter._id}`
        )
      } else {
        // Fallback to original behavior
        handleAccessContent(moduleRef, lessonRef)
      }
    },
    [getLessonProgressData, dispatch, handleAccessContent]
  )

  if (isLoading) {
    return (
      <div className="structured-contest">
        <div className="structured-contest-header">
          <div className="loading-block" style={{ width: 200, height: 40 }} />
        </div>
        <div className="structured-contest-content">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="loading-block"
              style={{ width: '100%', height: 60, marginBottom: 8 }}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="structured-contest">
      {/* Título e abas de visualização */}
      <div className="structured-contest-header">
        <div className="structured-contest-view-tabs">
          <button
            className={`structured-contest-view-tab ${viewMode === 'my-cycle' ? 'active' : ''}`}
            onClick={() => setViewMode('my-cycle')}
          >
            MEU CICLO
          </button>
          <button
            className={`structured-contest-view-tab ${
              viewMode === 'full-syllabus' ? 'active' : ''
            }`}
            onClick={() => setViewMode('full-syllabus')}
          >
            TODO O EDITAL
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="structured-contest-filters">
        <div className="structured-contest-filter-group">
          <label htmlFor="disciplineFilter" className="structured-contest-filter-label">
            DISCIPLINA/ASSUNTO
          </label>
          <div className="structured-contest-filter-input-wrapper">
            <input
              type="text"
              id="disciplineFilter"
              className="structured-contest-filter-input"
              placeholder="Buscar disciplina"
              value={searchFilter}
              onChange={handleSearchChange}
            />
            <AiOutlineSearch className="structured-contest-filter-search-icon" size={18} />
          </div>
        </div>
      </div>

      {/* Lista de disciplinas */}
      <div className="structured-contest-list">
        {filteredModules.length > 0 ? (
          filteredModules.map((module: ModuleWithChildren) => (
            <div key={module.ref} className="structured-contest-module">
              {/* Header da disciplina */}
              <div
                className={`structured-contest-module-header ${
                  expandedModules.has(module.ref) ? 'expanded' : ''
                }`}
                onClick={() => toggleModule(module.ref)}
              >
                <div className="structured-contest-module-toggle">
                  <AiOutlineCaretDown
                    size={14}
                    style={{
                      transition: 'transform 0.2s ease',
                      transform: expandedModules.has(module.ref)
                        ? 'rotate(0deg)'
                        : 'rotate(-90deg)',
                    }}
                  />
                </div>
                <span className="structured-contest-module-name">{module.subject}</span>
              </div>

              {/* Lista de aulas */}
              {expandedModules.has(module.ref) && (
                <div className="structured-contest-lessons">
                  {loadingModules.has(module.ref)
                    ? // Skeleton loading
                      Array.from({ length: 3 }).map((_, index) => (
                        <div key={index} className="structured-contest-lesson">
                          <div className="structured-contest-lesson-content">
                            <div
                              className="loading-block"
                              style={{ width: 20, height: 20, borderRadius: '50%' }}
                            />
                            <div
                              className="loading-block"
                              style={{ width: '60%', height: 16, marginLeft: 8 }}
                            />
                          </div>
                          <div className="structured-contest-lesson-actions">
                            <div className="loading-block" style={{ width: 80, height: 28 }} />
                            <div className="loading-block" style={{ width: 40, height: 16 }} />
                          </div>
                        </div>
                      ))
                    : module.children.map((lesson) => {
                        const progressData = getLessonProgressData(module.ref, lesson.ref)
                        const progress = progressData?.progress ?? lesson.progress ?? 0

                        return (
                          <div key={lesson.ref} className="structured-contest-lesson">
                            <div className="structured-contest-lesson-content">
                              <div className="structured-contest-lesson-info">
                                <span className="structured-contest-lesson-number">
                                  {(lesson.order + 1).toString().padStart(2, '0')}.
                                </span>
                                <span
                                  className={`structured-contest-lesson-name ${
                                    loadingLessonRef === lesson.ref ? 'loading' : ''
                                  }`}
                                  onClick={() => {
                                    if (progressData?.available.lock) {
                                      setSelectedModuleId(module.ref)
                                      freemiumModal.openModal()
                                      return
                                    }
                                    if (loadingLessonRef !== lesson.ref) {
                                      handleLessonNavigation(module.ref, lesson.ref)
                                    }
                                  }}
                                >
                                  {loadingLessonRef === lesson.ref
                                    ? 'Carregando...'
                                    : progressData?.heading || lesson.heading || lesson.name}
                                </span>
                              </div>
                            </div>
                            <div className="structured-contest-lesson-actions">
                              <span className="structured-contest-lesson-progress-text">
                                {Math.round(progress)}%
                              </span>
                            </div>
                          </div>
                        )
                      })}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="structured-contest-empty">
            <p>Nenhuma disciplina encontrada com os filtros aplicados.</p>
          </div>
        )}
      </div>
      {freemiumModal.renderModal()}
    </div>
  )
}

export default StructuredContest
